import React from 'react';
import { observer } from '../../../shared/observer';
import { debounce } from '../../../shared/debounce';
import { runBusy, isBusy } from '../../../shared/busy';
import { router } from '../../../shared/hashRouter';
import { AddressPicker } from './AddressPicker';
import { DateSlot } from './DateSlot';
import { AdditionalFields } from './AdditionalFields';
import { CheckoutSteps } from './CheckoutSteps';
import {
  requiredClientFields,
  anyClientFieldRequired,
  clientFieldErrors,
  clientFieldValues,
  clientInfoPatch,
  fioFromParts,
} from '../../../shared/clientFields';

// Шаг 1 — доставка. Аналог мобильного `client_app/.../Cart/Checkout/index.tsx`.
// Адрес, дата, период, доп. поля, ФИО, комментарий. По кнопке «Далее»
// валидирует ФИО + required additional fields + адрес/дату/период, пушит изменения на сервер и
// переходит на `#payment` (см. `PaymentForm.jsx`).
//
// При маунте конвертирует корзину в заказ (`v3/orders/start`).
class CheckoutFormView extends React.Component {
  state = {
    loading: true,
    error: null,
    fio: '',
    // Ф/И/О и email отдельными полями — когда компания требует их настройками приложения
    parts: { first: '', last: '', middle: '', email: '' },
    comment: '',
    errors: {},
    busy: {},
  };

  componentWillMount() {
    // Фоновый автосейв: ошибку глотаем — goNext/submit всё равно шлют
    // полный fieldsToUpdateOrder, так что потерянный автосейв восстановится.
    this.pushUpdate = debounce(() => this.props.order.updateCommonParams().catch(() => {}), 500);
  }

  async componentDidMount() {
    try {
      const orderLoaded = this.props.order.order && Object.keys(this.props.order.order).length > 0;
      if (!orderLoaded) {
        await this.props.order.convertCartToOrder();
      }
      const o = this.props.order.order || {};
      const ci = this.props.auth.clientInfo || {};
      const parts = clientFieldValues(ci);
      const init = {
        fio: this.props.order.fieldsToUpdateOrder.fio || ci.mobile_fio || o.fio || '',
        comment: this.props.order.fieldsToUpdateOrder.comment || o.comment || '',
      };
      if (this.nameInParts()) init.fio = fioFromParts(parts) || init.fio;
      this.setState(Object.assign({ loading: false, parts }, init));
      if (!this.props.order.fieldsToUpdateOrder.fio || !this.props.order.fieldsToUpdateOrder.comment) {
        this.props.order.setFieldsToUpdateOrder({ fio: init.fio, comment: init.comment });
      }
    } catch (err) {
      this.setState({ loading: false, error: err.message || 'Не удалось загрузить форму' });
    }
  }

  setField = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    this.setState({ [k]: v, errors: Object.assign({}, this.state.errors, { [k]: null }) });
    this.props.order.setFieldsToUpdateOrder({ [k]: v });
    this.pushUpdate();
  };

  // Компания требует что-то из карточки клиента (Ф/И/О, email) — тогда эти поля
  // идут в карточку, а сервер без них заказ не примет
  needsClientCard() {
    return anyClientFieldRequired(this.props.auth.companyInfo);
  }

  // Имя по частям (Имя, Фамилия, Отчество) вместо одного поля «Имя и фамилия»
  nameInParts() {
    return requiredClientFields(this.props.auth.companyInfo).first;
  }

  setPart = (k) => (e) => {
    const parts = Object.assign({}, this.state.parts, { [k]: e.target.value });
    const errors = Object.assign({}, this.state.errors, { [k]: null });
    if (k === 'email') {
      this.setState({ parts, errors });
      return;
    }
    const fio = fioFromParts(parts);
    this.setState({ parts, fio, errors: Object.assign(errors, { fio: null }) });
    this.props.order.setFieldsToUpdateOrder({ fio });
    this.pushUpdate();
  };

  // Валидация перед переходом на шаг оплаты. Зеркало мобильного
  // `handleNextStep` (Checkout/index.tsx:228): FIO + required additional fields.
  validate() {
    const errors = {};
    const fields = this.props.order.fieldsToUpdateOrder;
    if (this.needsClientCard()) {
      Object.assign(errors, clientFieldErrors(this.props.auth.companyInfo, this.state.parts));
    }
    if (!this.nameInParts() && (!fields.fio || !fields.fio.trim())) {
      errors.fio = 'Укажите ФИО';
    }
    const add = fields.additional_fields_collected || [];
    add.forEach((f, i) => {
      if (!f.is_required) return;
      const v = f.value;
      const empty = f.type === 'boolean' ? !v : (v == null || v === '');
      if (empty) errors['add_' + i] = 'Обязательное поле';
    });
    return errors;
  }

  // Адрес, дата и период: без них сервер не примет оплату (черновик перед
  // оплатой проверяется как оформленный заказ), а клиент узнал бы об этом
  // только на шаге оплаты. Тексты — как в ответе сервера (order_checkout_errors).
  deliveryError() {
    const f = this.props.order.fieldsToUpdateOrder;
    if (f.delivery_type !== 'delivery') return null;
    const noAddress = !f.address_id;
    const noTime = !f.date || !f.time_to;
    if (noAddress && noTime) return 'Укажите адрес и время доставки';
    if (noAddress) return 'Укажите адрес доставки';
    if (noTime) return 'Выберите дату и время доставки';
    return null;
  }

  // Фича включена у компании; нужен ли шаг на самом деле, решает сервер
  tareSignEnabled() {
    const info = (this.props.auth && this.props.auth.companyInfo) || {};
    return !!((info.cart_coupled || {}).tare_rent_checkout_sign);
  }

  goNext = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isBusy(this, 'submit')) return;
    const errors = this.validate();
    const deliveryError = this.deliveryError();
    if (Object.keys(errors).length || deliveryError) {
      this.setState({ errors, error: deliveryError });
      return;
    }
    this.setState({ error: null });
    runBusy(this, 'submit', async () => {
      this.pushUpdate.cancel();
      // Карточка клиента — источник правды для сервера: без неё он не примет заказ
      if (this.needsClientCard()) {
        await this.props.auth.updateClientInfo(clientInfoPatch(this.state.parts));
      }
      await this.props.order.updateCommonParams(this.props.order.fieldsToUpdateOrder);

      // Промежуточный этап подписания акта аренды тары. Запрос уходит только
      // у компаний, включивших фичу: иначе это лишний round-trip на каждом
      // оформлении заказа.
      if (this.tareSignEnabled()) {
        const res = await this.props.order.prepareRentAct();
        if (res && (res.status === 'unsigned' || res.status === 'stale')) {
          router.go('#tare_sign');
          return;
        }
      }

      router.go('#payment');
    }).catch((err) => this.setState({ error: err.message || 'Не удалось перейти к оплате' }));
  };

  // Отдельные поля по настройкам компании; подписи те же, что в «Настройках» профиля
  renderParts() {
    const company = this.props.auth.companyInfo;
    const need = requiredClientFields(company);
    // стили здесь, а не в scss: у каждого шаблона семейства newgen свои стили
    const field = (k, placeholder, type) => (
      <div key={k} style={{ marginBottom: 14 }}>
        <input
          className={'input__text' + (this.state.errors[k] ? ' input__text--error' : '')}
          type={type || 'text'}
          placeholder={placeholder}
          value={this.state.parts[k]}
          onChange={this.setPart(k)}
        />
        {this.state.errors[k] && <div className="ng-field-error">{this.state.errors[k]}</div>}
      </div>
    );
    return (
      <div>
        {need.first && field('first', 'Имя')}
        {need.last && field('last', 'Фамилия')}
        {need.middle && field('middle', 'Отчество')}
        {need.middleOptional && field('middle', 'Отчество (необязательно)')}
        {!need.first && this.renderFio()}
        {need.email && field('email', company.mobile_email_title || 'Email', 'email')}
      </div>
    );
  }

  // Прежнее одно поле — когда компания по частям имя не требует
  renderFio() {
    return (
      <div style={this.needsClientCard() ? { marginBottom: 14 } : undefined}>
        <input
          className={'input__text' + (this.state.errors.fio ? ' input__text--error' : '')}
          type="text"
          placeholder="Имя и фамилия"
          value={this.state.fio}
          onChange={this.setField('fio')}
        />
        {this.state.errors.fio && <div className="ng-field-error">{this.state.errors.fio}</div>}
      </div>
    );
  }

  render() {
    if (this.state.loading) return <div className="ng-cart"><p>Загрузка формы заказа…</p></div>;
    if (this.state.error && !this.props.order.order) {
      return <div className="ng-cart"><div className="order-message">{this.state.error}</div></div>;
    }

    return (
      <form className="ng-checkout" onSubmit={this.goNext}>
        <div className="ng-checkout__nav">
          <a href="#" onClick={(e) => { e.preventDefault(); router.go(''); }}>← Назад в корзину</a>
        </div>

        <h1>Оформление заказа</h1>
        <CheckoutSteps active={1} hasSign={this.tareSignEnabled()} />

        <AddressPicker />
        <DateSlot onChange={this.pushUpdate} />

        <section className="ng-checkout__section">
          <label>Контакт</label>
          {this.needsClientCard() ? this.renderParts() : this.renderFio()}
        </section>

        <AdditionalFields />

        <section className="ng-checkout__section">
          <label>Комментарий</label>
          <textarea
            className="input__text"
            placeholder="Особые пожелания"
            value={this.state.comment}
            onChange={this.setField('comment')}
            rows={3}
          />
        </section>

        {this.state.error && <div className="order-message">{this.state.error}</div>}

        <div className="ng-cart-actions">
          <button
            type="submit"
            className={'btn btn--block' + (isBusy(this, 'submit') ? ' is-busy' : '')}
            disabled={isBusy(this, 'submit')}
          >
            Далее
          </button>
        </div>
      </form>
    );
  }
}

export const CheckoutForm = observer(CheckoutFormView, ['cartStore', 'orderStore', 'authStore', 'addressStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/Checkout/CheckoutForm.jsx